/*
 * CSS for the shadow-DOM-isolated dialog.
 *
 * Colors are aliased once on :host from the host site's palette tokens
 * (--clr-base, --clr-contrast, --clr-accent-1, --clr-danger-*, etc.) with
 * light-theme fallbacks for sites without the palette. The aliases use a
 * --_ prefix so they don't collide with anything inherited from the host.
 * Custom properties inherit through the shadow boundary and survive the
 * host element's `all: initial`, so this Just Works on Scalebloom client
 * sites that define the palette.
 *
 * Theme switching (light ↔ dark) is driven by the host site swapping its
 * own --clr-* values — there's no prefers-color-scheme handling here.
 * var() resolution is dynamic, so the aliases re-evaluate on theme change.
 *
 * Lengths are px, never rem: rem resolves against the host page's <html>
 * font-size even inside the shadow root, and themes like OceanWP set it to
 * 62.5%, which shrinks the whole widget.
 *
 * ⚠️ SYNC: dashboard/src/AppearancePreview.svelte renders a static mock
 * of this dialog for the Appearance tab's live preview. Header layout,
 * accent dot, avatar swap, message bubbles, starter pills, send button —
 * all duplicated there with matching --_ vars. When you change any of
 * those visual rules here, change them there too.
 *
 * The HWB fallback values in the :host block below ALSO live in two more
 * places so the editor swatches match the preview:
 *   - dashboard/src/AppearancePreview.svelte (the .preview { --_* } block)
 *   - dashboard/src/Appearance.svelte        (WIDGET_DEFAULTS const)
 * Update all three together if these fallbacks ever change.
 */

:host {
  --_base: var(--clr-base, hwb(0 97% 3%));
  --_base-2: var(--clr-base-2, hwb(218 91% 5%));
  --_base-3: var(--clr-base-3, hwb(0 84% 16%));
  --_contrast: var(--clr-contrast, hwb(0 11% 89%));
  --_contrast-3: var(--clr-contrast-3, hwb(0 33% 67%));
  --_accent-1: var(--clr-accent-1, hwb(231 13% 0%));
  --_accent-2: var(--clr-accent-2, hwb(201 57% 0%));
  --_danger-base: var(--clr-danger-base, #ffc0ca);
  --_danger-contrast: var(--clr-danger-contrast, #8f0015);
  /* Overridden at runtime by contrast.ts when the accent is illegible. */
  --_on-accent: var(--_base);
  --_on-accent-2: var(--_contrast);
  --_accent-ink: var(--_accent-1);
}

.dialog {
  background: var(--_base);
  color: var(--_contrast);
  font: inherit;
  width: min(calc(100vw - 32px), 512px);
  height: min(80vh, 576px);
  border: 1px solid var(--_base-3);
  border-radius: 16px;
  margin: auto auto 16px;
  padding: 0;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 24px 48px -16px rgba(0, 0, 0, 0.25);
  /* Enter/leave animation. display and overlay both need allow-discrete so
     the dialog can transition between top-layer and not. @starting-style
     gives the entry frame; the base rules above are the exit (closed) frame. */
  opacity: 0;
  transform: translateY(8px);
  transition:
    opacity 180ms ease,
    transform 180ms ease,
    height 180ms ease,
    overlay 180ms ease allow-discrete,
    display 180ms ease allow-discrete;

  /* Booking calendar open: Calendly's stacked layout needs ~650px+. */
  &.booking {
    height: min(calc(100dvh - 32px), 800px);

    & .messages,
    & .starters,
    & .form,
    & .branding { display: none; }
  }

  @media (min-width: 768px) {
    margin: auto;
  }

  /* display: flex repeats in [open] and @starting-style on purpose — display
     is in the transition list, so each state must set it explicitly or the
     dialog flashes to display: block mid-transition. */
  &[open] {
    display: flex;
    opacity: 1;
    transform: translateY(0);
    @starting-style {
      display: flex;
      opacity: 0;
      transform: translateY(8px);
    }
  }

  &::backdrop {
    background: rgba(0, 0, 0, 0.35);
    opacity: 0;
    transition:
      opacity 180ms ease,
      overlay 180ms ease allow-discrete,
      display 180ms ease allow-discrete;
  }
  &[open]::backdrop {
    opacity: 1;
    @starting-style {
      opacity: 0;
    }
  }
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  border-bottom: 1px solid var(--_base-3);
  flex-shrink: 0;
}
.title {
  display: flex;
  align-items: center;
  gap: 8px;

  &::before {
    content: "";
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--_accent-1);
  }
  /* Image avatar takes the dot's place when the site has a trigger image. */
  &:has(.title-avatar)::before {
    display: none;
  }
}
.title-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
}
.actions {
  display: flex;
  align-items: center;
  gap: 4px;
}
.close,
.clear {
  background: none;
  border: none;
  padding: 6px;
  border-radius: 8px;
  color: var(--_contrast);
  cursor: pointer;
  display: grid;
  place-items: center;
  font: inherit;

  &:hover {
    background: var(--_base-2);
  }
  & svg {
    width: 18px;
    height: 18px;
  }
}

.messages {
  flex: 1;
  overflow-y: auto;
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.empty {
  color: var(--_contrast-3);
  text-align: center;
  margin: auto 0;
  padding: 20px 0;
}

.starters {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 0 20px 16px;
}
.starter {
  border: 1px solid var(--_base-3);
  background: var(--_base-2);
  color: var(--_contrast);
  padding: 6px 12px;
  border-radius: 999px;
  cursor: pointer;
  font: inherit;
  font-size: 13px;

  &:hover {
    border-color: var(--_accent-1);
  }
}

.message {
  max-width: 85%;
  padding: 10px 14px;
  border-radius: 12px;
  font-size: 15px;
  line-height: 1.45;
  white-space: pre-wrap;
  word-wrap: break-word;
}
.message-user {
  align-self: flex-end;
  background: var(--_accent-2);
  color: var(--_on-accent-2);
}
.message-assistant {
  align-self: flex-start;
  background: var(--_base-2);
  color: var(--_contrast);
}
.message a {
  color: var(--_accent-ink);
  text-underline-offset: 2px;
}
.message-error {
  align-self: center;
  background: var(--_danger-base);
  color: var(--_danger-contrast);
  font-size: 14px;
  padding: 8px 12px;
  border-radius: 8px;
}

/* Lives inside the assistant bubble, which is white-space: pre-wrap. */
.booking-card {
  display: flex;
  align-items: center;
  gap: 12px;
  inline-size: 100%;
  box-sizing: border-box;
  margin-block-start: 10px;
  padding: 12px 14px;
  border: 1px solid var(--_base-3);
  border-radius: 12px;
  background: var(--_base);
  color: var(--_contrast);
  font: inherit;
  text-align: start;
  white-space: normal;

  & svg {
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    color: var(--_accent-ink);
  }
  & .chevron { color: var(--_contrast-3); }
  &:is(button) {
    cursor: pointer;
    &:hover { border-color: var(--_accent-1); }
  }
}
.booking-card-text {
  flex: 1;
  display: flex;
  flex-direction: column;
  line-height: 1.3;

  & span {
    font-size: 13px;
    color: var(--_contrast-3);
  }
}

/* Contact form (show_contact_form). Sits bare inside the pre-wrap bubble,
   which goes full width so four fields aren't squeezed. */
.message-assistant:has(.contact-card) {
  inline-size: 100%;
  max-width: none;
  box-sizing: border-box;
}
.contact-card {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-block-start: 14px;
  white-space: normal;

  & input[name="website"] {
    position: absolute;
    inset-inline-start: -9999px;
    inline-size: 1px;
    block-size: 1px;
    opacity: 0;
    pointer-events: none;
  }
}
.contact-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(144px, 1fr));
  gap: 10px;
}
.contact-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 13px;
  color: var(--_contrast-3);

  & :is(input, textarea) {
    inline-size: 100%;
    box-sizing: border-box;
    border: 1px solid var(--_base-3);
    border-radius: 8px;
    background: var(--_base);
    color: var(--_contrast);
    padding: 8px 10px;
    font: inherit;
    font-size: 15px;

    &:focus {
      outline: none;
      border-color: var(--_accent-1);
    }
  }
  & textarea {
    resize: vertical;
    field-sizing: content;
    min-block-size: 72px;
    max-block-size: 160px;
  }
}
.contact-error {
  margin: 0;
  font-size: 13px;
  color: var(--_danger-contrast);
}
.contact-send {
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  background: var(--_accent-1);
  color: var(--_on-accent);
  font: inherit;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: progress;
  }
}

.booking-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-block-size: 0;

  &[hidden] { display: none; }
}
.booking-back {
  display: flex;
  align-items: center;
  gap: 4px;
  align-self: flex-start;
  margin: 8px 12px;
  padding: 6px 8px;
  border: none;
  border-radius: 8px;
  background: none;
  color: var(--_contrast);
  font: inherit;
  font-size: 14px;
  cursor: pointer;

  &:hover { background: var(--_base-2); }
  & svg {
    width: 18px;
    height: 18px;
  }
}
/* The loading line sits under the iframe and shows until Calendly paints
   over it, or for good if the host's CSP blocks the frame. */
.booking-frame {
  position: relative;
  flex: 1;
  border-block-start: 1px solid var(--_base-3);

  & iframe {
    position: absolute;
    inset: 0;
    inline-size: 100%;
    block-size: 100%;
    border: 0;
  }
}
.booking-loading {
  margin: 0;
  padding: 20px;
  text-align: center;
  font-size: 14px;
  color: var(--_contrast-3);

  & a { color: var(--_accent-ink); }
}

.form {
  display: flex;
  gap: 8px;
  align-items: flex-end;
  padding: 12px 16px 16px;
  border-top: 1px solid var(--_base-3);
  flex-shrink: 0;
}
/* Off-screen rather than display:none so bots that skip hidden fields still
   find and fill it. Selected by attribute (no telltale class name). */
.form input[name="website"] {
  position: absolute;
  left: -9999px;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
}
.input {
  flex: 1;
  resize: none;
  border: 1px solid var(--_base-3);
  border-radius: 12px;
  background: var(--_base);
  color: var(--_contrast);
  padding: 10px 14px;
  font: inherit;
  font-size: 15px;
  line-height: 1.4;
  max-height: 128px;
  field-sizing: content;

  &:focus {
    outline: none;
    border-color: var(--_accent-1);
  }
}
.send {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  background: var(--_accent-1);
  color: var(--_on-accent);
  border: none;
  border-radius: 12px;
  display: grid;
  place-items: center;
  cursor: pointer;
  transition: opacity 0.15s;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
  & svg {
    width: 18px;
    height: 18px;
  }
}

/* "Powered by Chatporch" footer link. Only rendered when the agent's config
   says showBranding (free tier; staff can override per agent). When present,
   it eats into the form's bottom padding so the dialog doesn't grow taller.
   The padding-top + deeper negative margin cancel out visually but pad the
   hit box to ~25px for WCAG 2.2 target size (2.5.8). */
.branding {
  flex-shrink: 0;
  margin-top: -14px;
  text-align: center;
  padding: 6px 16px 8px;
  font-size: 11px;
  color: var(--_contrast-3);
  text-decoration: none;

  & strong { font-weight: 600; }
  &:hover { color: var(--_contrast); text-decoration: underline; }
}
